import React, { useState, useRef, useEffect } from 'react';

const Gallery = () => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [images, setImages] = useState([]);
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    const modalContentRef = useRef(null);
    const uploadUrl = process.env.REACT_APP_GALLERY_UPLOAD_URL;

    useEffect(() => {
        fetch('/api/gallery/list')
            .then(res => res.json())
            .then(imageList => setImages(imageList))
            .catch(error => console.error('Error fetching gallery images:', error));
    }, []);

    const openImage = (index) => {
        setSelectedImageIndex(index);
    };

    const closeImage = () => {
        setSelectedImageIndex(null);
    };

    const nextImage = (e) => {
        if (e) e.stopPropagation();
        setSelectedImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    };

    const prevImage = (e) => {
        if (e) e.stopPropagation();
        setSelectedImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    };

    const handleKeyDown = (e) => {
        if (selectedImageIndex === null) return;

        if (e.key === 'ArrowRight') {
            nextImage(e);
        } else if (e.key === 'ArrowLeft') {
            prevImage(e);
        } else if (e.key === 'Escape') {
            closeImage();
        }
    };

    // Touch handlers for swipe functionality
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!touchStartX.current || !touchEndX.current) return;

        const diff = touchStartX.current - touchEndX.current;
        const threshold = 50; // Minimum distance to trigger swipe

        if (diff > threshold) {
            // Swiped left, go to next image
            nextImage();
        } else if (diff < -threshold) {
            // Swiped right, go to previous image
            prevImage();
        }

        // Reset touch coordinates
        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Add event listeners with passive option
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        // Set up touch events with proper passive options for the modal
        if (selectedImageIndex !== null && modalContentRef.current) {
            const modalContent = modalContentRef.current;

            modalContent.addEventListener('touchstart', handleTouchStart, { passive: true });
            modalContent.addEventListener('touchmove', handleTouchMove, { passive: true });
            modalContent.addEventListener('touchend', handleTouchEnd, { passive: true });

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                modalContent.removeEventListener('touchstart', handleTouchStart);
                modalContent.removeEventListener('touchmove', handleTouchMove);
                modalContent.removeEventListener('touchend', handleTouchEnd);
            };
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedImageIndex, handleKeyDown, handleTouchEnd, handleTouchStart, handleTouchMove]);

    return (
        <div className="gallery-container">
            <h2 className="gallery-heading">BBQ Gallery</h2>
            {uploadUrl && (
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <a
                        href={uploadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            backgroundColor: '#f4511e',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontSize: '16px',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        }}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ marginRight: '4px' }}
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload your photos and videos
                    </a>
                </div>
            )}
            <div className="gallery-grid">
                {images.map((image, index) => (
                    <div key={index} className="gallery-item" onClick={() => openImage(index)}>
                        <img
                            src={`/gallery/${image}`}
                            alt={`BBQ ${index + 1}`}
                            className="gallery-image"
                        />
                    </div>
                ))}
            </div>

            {selectedImageIndex !== null && (
                <div className="modal" onClick={closeImage}>
                    <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                        ref={modalContentRef}
                    >
                        <img
                            src={`/gallery/${images[selectedImageIndex]}`}
                            alt={`BBQ ${selectedImageIndex + 1}`}
                            className="modal-image"
                        />
                        <button className="close-button" onClick={closeImage}>✖</button>
                        <button className="nav-button prev-button" onClick={prevImage}>❮</button>
                        <button className="nav-button next-button" onClick={nextImage}>❯</button>
                        <div className="image-counter">{selectedImageIndex + 1} / {images.length}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery; 